import React, {FC, useState, useEffect} from "react"

import {useTheme} from "@irati/ui/src/global/useTheme"
import {Card} from "@irati/ui/src/Layout"
import FormattedMessage, {FormattedCurrency} from "@irati/utils/lang"
import {Body, H4, Label} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"

import {useListOrdersQuery} from "@irati/payrollpay/hooks"
import moment from "moment"
import Button from "@irati/ui/src/Button"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import {FlexChildLabel, StyledBody} from "./styled"

interface Props {
  currentOrder: string
  employeeNumber: string
}

const OtherOrders: FC<Props> = ({currentOrder, employeeNumber}) => {
  const [total, setTotal] = useState<number>(0)
  const [showAll, setShowAll] = useState(false)
  const theme = useTheme()

  const [listOrders, {data}] = useListOrdersQuery({
    employee_number: employeeNumber
  })

  useEffect(() => {
    listOrders()
  }, [])

  useEffect(() => {
    if (data?.orders) {
      setTotal(data?.orders?.length - 1)
    }
  }, [data?.orders])

  const orders = data?.orders.filter((order) => order.order_number !== currentOrder) || []
  const ordersToRender = showAll ? orders : orders.slice(0, 2)

  return (
    <Card padding={[2, 2]} marginTop={2} marginLeft={2}>
      <H4 marginBottom={1.5}>
        {orders.length ? (
          <FormattedMessage id="admin.components.orderManagement.otherOrders.title1" values={{total}} />
        ) : (
          <FormattedMessage id="admin.components.orderManagement.otherOrders.title2" />
        )}
      </H4>
      {orders.length ? (
        ordersToRender.map((item) => {
          return (
            <Card padding={1} marginBottom={1}>
              <Flex direction="column">
                <Flex>
                  <Label color={theme.colors.greys.grey50}>{moment(item.effective_date).format("MMM YY")}</Label>
                  <Label color={theme.colors.greys.grey50} marginLeft marginRight>
                    •
                  </Label>
                  <Label color={theme.colors.greys.grey50}>{item.benefit_type}</Label>
                </Flex>
                {item.products.map((product) => {
                  return <StyledBody small>{product?.item_name}</StyledBody>
                })}
                <Flex justifyContent="space-between">
                  <FlexChildLabel color={theme.colors.greys.grey50}>
                    <FormattedMessage id="admin.components.orderManagement.otherOrders.label.orderNr" />
                  </FlexChildLabel>
                  <FlexChildLabel>{item.order_number}</FlexChildLabel>
                </Flex>
                <Flex justifyContent="space-between">
                  <FlexChildLabel color={theme.colors.greys.grey50}>
                    <FormattedMessage id="admin.components.orderManagement.otherOrders.label.salary" />
                  </FlexChildLabel>
                  <FlexChildLabel>
                    <FormattedCurrency currency="GBP" value={item?.amount_per_pay_period} />{" "}
                    <FormattedMessage id="admin.components.orderManagement.otherOrders.label.perMonth" />
                  </FlexChildLabel>
                </Flex>
                <Flex justifyContent="space-between">
                  <FlexChildLabel color={theme.colors.greys.grey50}>
                    <FormattedMessage id="admin.components.orderManagement.otherOrders.label.orderAmount" />
                  </FlexChildLabel>
                  <FlexChildLabel>
                    <FormattedCurrency currency="GBP" value={item.total_amount_to_recover} />
                  </FlexChildLabel>
                </Flex>
                <Flex justifyContent="space-between">
                  <FlexChildLabel color={theme.colors.greys.grey50}>
                    <FormattedMessage id="admin.components.orderManagement.otherOrders.label.approvalStatus" />
                  </FlexChildLabel>
                  <FlexChildLabel>{item.approval_status}</FlexChildLabel>
                </Flex>
                <Flex justifyContent="space-between">
                  <FlexChildLabel color={theme.colors.greys.grey50}>
                    <FormattedMessage id="admin.components.orderManagement.otherOrders.label.orderStatus" />
                  </FlexChildLabel>
                  <FlexChildLabel>{item.order_status}</FlexChildLabel>
                </Flex>
              </Flex>
            </Card>
          )
        })
      ) : (
        <Body small>
          <FormattedMessage id="admin.components.orderManagement.otherOrders.noOrders" />
        </Body>
      )}
      {orders.length > 2 && (
        <Button tertiary marginTop={1} onClick={() => setShowAll(!showAll)}>
          <FormattedMessage id="admin.components.orderManagement.otherOrders.showAll" />
          <Icon marginLeft={1} color={theme.colors.primary2} icon={showAll ? IconTypes.chevronUp : IconTypes.chevronDown} size={14} />
        </Button>
      )}
    </Card>
  )
}

export default OtherOrders
